import { Injectable, HostListener, Directive } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { KP2AppInsightsProperties } from '../models/AppInsights';
import { delayWhen } from 'rxjs/operators';
import { BehaviorSubject, interval, of } from 'rxjs';
import { environment } from '@env/environment';
import { ApplicationInsightEvent } from '../enums';
import { toUpper } from 'lodash';
import { AuthService } from './auth.service';
import { ILogger } from '../models/AppInsights/ILogger';

@Directive()
@Injectable({
  providedIn: 'root',
})
export class AppInsightsServiceDirective implements ILogger {
  private _properties?: KP2AppInsightsProperties;
  private _trace: any;
  private isInitialized = new BehaviorSubject<boolean>(false);
  private appInsights?: ApplicationInsights;

  constructor(public authService: AuthService) {}

  public addProperties(value: any): void {
    this._properties = Object.assign({}, this._properties, value);
  }

  /** sets trace information to be used in error loging for unhandled exception. should set to null if done tracing a code flow/process to avoid leaking trace logs to unrelated exceptions */
  public setErrorTrace(value: any): void {
    this._trace = value;
  }

  public initialize(): void {
    if (window.location.hostname !== 'localhost') {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.instrumentationKey,
          extensionConfig: {
            ['AppInsightsCfgSyncPlugin']: {
              cfgUrl: '', // this will block fetching from default cdn
            },
          },
        },
      });
      this.appInsights.loadAppInsights();
      this.isInitialized.next(true);
    }
  }

  public logEvent<T extends object>(name: ApplicationInsightEvent | string, properties?: T): void {
    if (window.location.hostname !== 'localhost') {
      this.isInitialized.pipe(delayWhen((val) => (val ? interval(1000) : of()))).subscribe(() => {
        const prop = {
          Tenant: toUpper(environment.tenant),
          ...this._properties,
          ...properties,
        };
        this.appInsights?.trackEvent({ name: name }, prop);
      });
    }
  }

  public logError(error: Error, addDetails: any = {}): void {
    if (window.location.hostname !== 'localhost') {
      const sessionInfo: { [key: string]: any } = {};

      if (this.authService.jwt) {
        sessionInfo['userGuid'] = this.authService.currentUser.guid;
        sessionInfo['sessionId'] = this.authService.currentUser.sessionId;
      }

      if (this.authService.appToken) {
        sessionInfo['userGuid'] = this.authService.currentUser.guid;
      }

      let trace = {};
      if (this._trace) {
        trace = this._trace;
      }

      this.appInsights?.trackException({
        exception: error,
        properties: {
          origin: 'EnergyPortal',
          location: window.location.href,
          ...sessionInfo,
          ...trace,
          ...addDetails,
        },
      });
    }
  }

  public logDebug(traceId: string, data = {}): void {
    if (environment.debugLog) {
      this.appInsights?.trackTrace(
        { message: traceId },
        {
          origin: 'EnergyPortal',
          location: window.location.href,
          ...data,
        }
      );
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  flush() {
    this.appInsights?.flush();
  }
}
